import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { AxiosError } from 'axios';

import { reverse } from 'named-urls';

import API from 'utils/api';
import RootStore from 'stores/Root';
import { ProtectorType } from 'core/constants';
import { AnswerTypes, AttachmentType } from 'utils/api/modules/incidents/incidents.types';
import { EntityFormType, EnumOptions, FormBuilderProperties } from 'utils/api/modules/entities/entities.types';
import { apiErrorsWithKeys } from 'utils/api/errors';
import Logger from 'utils/logger';
import routes from 'core/routes';
import { PublicSubmissionOptions } from 'stores/FormBuilderSettings/types';
import { publicEventFormDB } from 'utils/indexedDB/publicEventFormDB';

import AuditQuestionActivities from 'stores/AuditQuestionActivities';
import { timezoneStore } from 'stores/Timezone';
import { getEntitySubmissionConfig, isAuditsSubmission } from 'containers/EntitiesFormSubmission/config';

import DraftActivities from './DraftActivities';
import SubmissionActivities from './SubmissionActivities';
import {
  RepeatingGroupErrors,
  hasRepeatingGroupErrors,
  validateRepeatingGroupEntries,
} from 'containers/EntitiesFormSubmission/components/RepeatingGroupField/repeatingGroupValidation';

import { allRequiredFieldsFilled, determineNextSection, fieldIsRequired, invalidFields } from './helpers';
import { LookupFieldsFetcher } from './LookupFieldsFetcher';
import FileUploadManager, { FileUploadStatus } from './FileUploadManager';

export interface EntitySubmissionDraftType {
  uuid: string;
  stack: string[];
  hasNext?: boolean;
  updatedAt: string;
  createdAt: string;
  formVersion: string;
  form: EntityFormType;
  content: Record<string, AnswerTypes>;
  publicSubmissionDetails?: PublicSubmissionUserDetails;
  offlineSubmission?: boolean;
  // Public payloads only; absent from one the service worker cached before the field existed.
  timezoneModelEnabled?: boolean;
  // AUDITS only: the in-progress record this draft belongs to, which an action's link needs.
  entity?: string | null;
}

type AutosaveQueueEntry = {
  value: AnswerTypes;
  type?: ProtectorType;
};

export type PublicSubmissionUserDetails = {
  name: string | null;
  email: string | null;
  phone: string | null;
};

export default class EntitiesFormSubmission {
  rootStore: RootStore;

  api: typeof API;

  entityType: string | null = null;

  @observable data: EntitySubmissionDraftType | null = null;

  @observable previewData: EntitySubmissionDraftType | null = null;

  @observable isLoading: boolean = false;

  @observable isError: boolean = false;

  @observable updating: boolean = false;

  @observable submitting: boolean = false;

  @observable autoSaving: boolean = false;

  @observable showPreview: boolean = false;

  @observable requiredNotFilled: string[] = [];

  // Repeating-group answers are 2D (entries × sub-fields), so their required errors can't ride the flat
  // `requiredNotFilled` list — keyed by questionKey -> [entryIndex][subFieldKey].
  @observable repeatingGroupErrors: Record<string, RepeatingGroupErrors> = {};

  @observable showRedirectModal: boolean = false;

  @observable showSubmittedModal: boolean = false;

  @observable draftActivities: DraftActivities | null = null;

  @observable questionActivities?: AuditQuestionActivities;

  // An ID that is unique for each form (the one used in the url)
  @observable publicSubmissionUuid: string = '';

  @observable isPublicSubmission: boolean = false;

  // An ID that is unique for each form session (the one used in the indexedDB)
  @observable publicFormSessionId: string = '';

  @observable publicSubmissionUserDetailsFields: PublicSubmissionOptions | null = null;

  @observable displayUserDetailsStep: boolean = false;

  @observable userDetailsRequiredNotFilled: string[] = [];

  @observable userDetailsErrors: Record<string, string[]> = {};

  @observable contentErrors: Record<string, string[]> = {};

  @observable displaySuccessPage: boolean = false;

  @observable displayOfflineSuccessPage: boolean = false;

  @observable displayReloadPage: boolean = false;

  @observable submissionActivities?: SubmissionActivities;

  @observable showConfirmPopup = false;

  @observable showDraftContinuationModal = false;

  @observable showMissingFilesModal = false;

  @observable existingDraftSessionId: string | null = null;

  @observable publicSubmissionUserDetails?: {
    name: string | null;
    email: string | null;
    phone: string | null;
  } = {
    name: null,
    email: null,
    phone: null,
  };

  @observable fileUploadManager: FileUploadManager = new FileUploadManager();

  private lookupFieldsFetcher: LookupFieldsFetcher;

  // Single-flight: overlapping PATCHes let a slow response revert a newer answer.
  private autosaveQueue = new Map<string, AutosaveQueueEntry>();

  private autosaveInFlight = false;

  private autosaveDrain: Promise<void> | null = null;

  // Edits whose debounce hasn't fired yet — keeps Next/Submit disabled from the keystroke.
  private pendingEdits = new Set<string>();

  // Bumped on every local write, so a failed save can tell whether the field moved on.
  private writeSeq = 0;

  private keyWriteSeq = new Map<string, number>();

  // Revert target for a failed save, held only while a write is outstanding for that question.
  private lastSaved = new Map<string, AnswerTypes>();

  constructor(rootStore: RootStore, api: typeof API) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.api = api;
    this.lookupFieldsFetcher = new LookupFieldsFetcher(api);
  }

  get currentSection(): string {
    return this.data?.stack[this.data.stack.length - 1] || '';
  }

  private repeatingProperties(questionKey: string): {
    order: string[];
    properties: Record<string, { required?: boolean; maximum?: number }>;
  } {
    const question = this.data!.form.form.properties[this.currentSection].properties[questionKey];
    return (
      question.properties as unknown as {
        repeatingGroup: { order: string[]; properties: Record<string, { required?: boolean; maximum?: number }> };
      }
    ).repeatingGroup;
  }

  // Validate every repeating-group question in the current section before advancing. Populates
  // `repeatingGroupErrors` (2D) so the offending sub-fields go red; returns true when all are valid.
  @action.bound
  validateRepeatingGroupsForCurrentSection(): boolean {
    if (!this.data) return true;

    const section = this.data.form.form.properties[this.currentSection];
    const errors: Record<string, RepeatingGroupErrors> = {};

    section.order.forEach((questionKey) => {
      if (section.properties[questionKey].questionType !== ProtectorType.RepeatingGroup) return;

      const entries = (this.data!.content[questionKey] as unknown as Record<string, AnswerTypes>[]) || [];
      const entryErrors = validateRepeatingGroupEntries(entries, this.repeatingProperties(questionKey));
      if (hasRepeatingGroupErrors(entryErrors)) {
        errors[questionKey] = entryErrors;
      }
    });

    this.repeatingGroupErrors = errors;
    return Object.keys(errors).length === 0;
  }

  // Live-clear a question's errors as the user fills them in — only once they've been surfaced by a
  // blocked advance, so errors never appear pre-emptively.
  @action.bound
  revalidateRepeatingGroup(questionKey: string, entries: Record<string, AnswerTypes>[]): void {
    if (!this.data || !this.repeatingGroupErrors[questionKey]) return;

    const entryErrors = validateRepeatingGroupEntries(entries, this.repeatingProperties(questionKey));
    const next = { ...this.repeatingGroupErrors };
    if (hasRepeatingGroupErrors(entryErrors)) {
      next[questionKey] = entryErrors;
    } else {
      delete next[questionKey];
    }
    this.repeatingGroupErrors = next;
  }

  @computed
  get missingAttachmentFields(): { key: string; title: string }[] {
    if (!this.data?.form?.form?.properties || !this.data?.content) {
      return [];
    }

    const missing: { key: string; title: string }[] = [];
    const formProperties = this.data.form.form.properties;

    Object.entries(formProperties).forEach(([, sectionProps]) => {
      if (!sectionProps.properties) return;

      Object.entries(sectionProps.properties).forEach(([questionKey, questionProps]) => {
        if (questionProps.questionType !== ProtectorType.Attachment) return;

        const value = this.data?.content[questionKey];
        if (!Array.isArray(value) || value.length === 0) return;

        const attachments = value as AttachmentType[];
        const fieldUploads = this.fileUploadManager.getFieldUploads(questionKey);

        const hasMissingFiles = attachments.some((att) => {
          // If attachment has a URL, it's already uploaded to the server
          if (att.url) return false;
          // Otherwise, check if we have the file in the upload manager
          return !fieldUploads.some((upload) => upload.file.name === att.filename);
        });

        if (hasMissingFiles) {
          missing.push({ key: questionKey, title: questionProps.description || questionKey });
        }
      });
    });

    return missing;
  }

  @action.bound
  dismissMissingFilesModal(): void {
    this.showMissingFilesModal = false;
  }

  @action.bound
  setDisplayReloadPage(display: boolean): void {
    runInAction(() => {
      this.displayReloadPage = display;
    });
  }

  @action.bound
  async moveToFirstSectionFromUserDetails(): Promise<void> {
    if (!this.data || !this.entityType) {
      return;
    }

    // send the user details to the server publicSubmissionDetails
    try {
      const { data } = await this.api.entities.updateEntityFormAutosave(
        this.entityType,
        this.isPublicSubmission,
        this.publicSubmissionUuid,
        {
          content: this.data.content,
          stack: this.data.stack,
          publicSubmissionDetails: this.publicSubmissionUserDetails,
          publicFormSessionId: this.publicFormSessionId,
        }
      )();

      if (this.checkFormVersion(data.formVersion)) {
        this.autoSaving = false;
        return;
      }

      runInAction(() => {
        this.data = data;
        this.displayUserDetailsStep = false;
      });

      // Update IndexedDB for offline access (similar to initial form load)
      // Always update IndexedDB for public submissions to keep it in sync
      if (this.isPublicSubmission) {
        // Store the form data in IndexedDB for offline access
        publicEventFormDB
          .upsertPublicEventForm(this.publicSubmissionUuid, data)
          .then(() => {
            Logger.debug('Public event form autosave stored in IndexedDB:', this.publicSubmissionUuid);
          })
          .catch((error) => {
            Logger.error('Failed to store public event form autosave in IndexedDB:', this.publicSubmissionUuid, error);
            // Don't fail the autosave if IndexedDB fails
          });

        // Update the submission record
        if (this.publicFormSessionId) {
          publicEventFormDB
            .updatePublicEventSubmission(this.publicFormSessionId, this.publicSubmissionUuid, {
              content: data.content,
              stack: data.stack,
              publicSubmissionDetails: data.publicSubmissionDetails,
            })
            .then(() => {
              Logger.debug('Submission record updated in IndexedDB for session:', this.publicFormSessionId);
            })
            .catch((error) => {
              Logger.error('Failed to update IndexedDB record for session:', this.publicFormSessionId, error);
              // Don't fail the autosave if IndexedDB fails
            });
        }
      }
    } catch (error) {
      const e = error as AxiosError;
      const responseData = e.response?.data as { publicSubmissionDetails?: Record<string, string[]> } | undefined;

      if (e.response?.status === 400 && responseData?.publicSubmissionDetails) {
        runInAction(() => {
          this.userDetailsRequiredNotFilled = Object.keys(responseData.publicSubmissionDetails || {});
          this.userDetailsErrors = responseData.publicSubmissionDetails || {};
        });
        this.rootStore.notification.enqueueErrorSnackbar('Please check your details and try again.');
        return;
      }

      Logger.error(apiErrorsWithKeys(e, 'Failed to save changes'));
      this.rootStore.notification.enqueueErrorSnackbar('Failed to save changes');
    }
  }

  @action.bound
  checkIfAllRequiredFieldsAreFilled(): boolean {
    // Reset the array before checking
    this.userDetailsRequiredNotFilled = [];

    const fieldsToCheck: Array<keyof PublicSubmissionUserDetails> = ['name', 'email', 'phone'];

    fieldsToCheck.forEach((field) => {
      if (
        this.publicSubmissionUserDetailsFields?.[field] &&
        (!this.publicSubmissionUserDetails?.[field] || this.publicSubmissionUserDetails[field] === '')
      ) {
        this.userDetailsRequiredNotFilled.push(field);
      }
    });

    return this.userDetailsRequiredNotFilled.length === 0;
  }

  @action.bound
  setPendingFiles(fieldKey: string, files: File[]): void {
    this.fileUploadManager.addFiles(fieldKey, files);
  }

  @action.bound
  clearPendingFiles(fieldKey?: string): void {
    if (fieldKey) {
      this.fileUploadManager.clearField(fieldKey);
    } else {
      this.fileUploadManager.clearAll();
    }
  }

  @action.bound
  removePendingFile(fieldKey: string, filename: string): void {
    this.fileUploadManager.removeFile(fieldKey, filename);
  }

  @action.bound
  resetFileUploadState(fieldKey: string, filename: string): void {
    this.fileUploadManager.updateStatus(fieldKey, filename, FileUploadStatus.Pending);
  }

  @action.bound
  saveUserDetails(key: 'name' | 'email' | 'phone', value: string | null): void {
    if (!this.publicSubmissionUserDetails) {
      return;
    }
    // if value is empty string set it to null
    if (value === '') {
      this.publicSubmissionUserDetails[key] = null;
    } else {
      this.publicSubmissionUserDetails[key] = value;
    }

    Object.keys(this.publicSubmissionUserDetails).forEach((k) => {
      if (
        this.publicSubmissionUserDetails?.[k as keyof PublicSubmissionUserDetails] === null ||
        this.publicSubmissionUserDetails?.[k as keyof PublicSubmissionUserDetails] === '' ||
        !this.publicSubmissionUserDetailsFields?.[k as keyof PublicSubmissionOptions]
      ) {
        delete this.publicSubmissionUserDetails?.[k as keyof PublicSubmissionUserDetails];
      }
    });

    this.userDetailsRequiredNotFilled = this.userDetailsRequiredNotFilled.filter((item) => item !== key);
    // Clear error messages for this field
    if (this.userDetailsErrors[key]) {
      delete this.userDetailsErrors[key];
    }
  }

  @action.bound
  async loadPublicSubmission(publicUuid: string): Promise<void> {
    this.entityType = 'events';
    this.isLoading = true;
    this.isPublicSubmission = true;
    this.publicSubmissionUuid = publicUuid;

    // This might be already set if the user decided to continue with the draft
    if (!this.publicFormSessionId) {
      // Generate a new session ID for each form session, this is very important for the SW to work offline
      this.publicFormSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    try {
      this.submissionActivities = new SubmissionActivities(this.rootStore, this.api, this.entityType);

      const { data, status } = await this.api.entities.getPublicSubmission(
        this.entityType,
        publicUuid,
        this.publicFormSessionId
      )();

      const hasNext = data.hasNext || data.stack?.length > 1;
      const draftSessionId = data?.offlineSubmission ? await this.checkForExistingDrafts(publicUuid) : null;

      runInAction(() => {
        this.data = { ...data, stack: data.stack.slice(0, 1), hasNext };
        timezoneStore.applyPublicFlag(data.timezoneModelEnabled);

        // Set session ID on FileUploadManager for persistence
        this.fileUploadManager.setSessionId(this.publicFormSessionId);

        // Check for existing drafts before creating a new session
        // If found, break the flow, display modal
        // If user wants to continue with the draft, set the session ID to the existing draft's session ID
        // If user wants to start a new submission, delete the existing draft record from the IndexedDB
        if (draftSessionId && draftSessionId !== this.publicFormSessionId) {
          runInAction(() => {
            this.setExistingDraftSessionId(draftSessionId);
            this.showDraftContinuationModal = true;
          });
        }
        // This block won't execute if user decided to continue with the draft
        else {
          data?.offlineSubmission &&
            this.lookupFieldsFetcher.fetchAndStoreLookupFields(publicUuid, data.form.form.properties);
          this.updateUserDetailsStep();

          // Create records in IndexedDB if the form is served from the API, not the service worker
          if (status !== 202) {
            // Store the form data in IndexedDB for offline access
            publicEventFormDB
              .upsertPublicEventForm(publicUuid, data)
              .then(() => {
                Logger.debug('Public event form stored in IndexedDB:', publicUuid);
              })
              .catch((error) => {
                Logger.error('Failed to store public event form in IndexedDB:', publicUuid, error);
                // Don't fail the form loading if IndexedDB fails
              });

            // Only create a new IndexedDB record for this session if no existing draft was found
            // Don't create empty records when drafts exist
            publicEventFormDB
              .createPublicEventSubmission(this.publicFormSessionId, publicUuid)
              .then(() => {
                Logger.debug('New submission record created in IndexedDB for session:', this.publicFormSessionId);
              })
              .catch((error) => {
                Logger.error('Failed to create IndexedDB record for session:', this.publicFormSessionId, error);
                // Don't fail the form loading if IndexedDB fails
              });

            // Clean up old draft submissions (older than 7 days) to prevent IndexedDB bloat
            publicEventFormDB.deleteOldSubmissions(7).then((deletedCount) => {
              if (deletedCount > 0) {
                Logger.debug(`Cleaned up ${deletedCount} old draft submissions from IndexedDB`);
              }
            });
          }
        }
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
        this.isError = true;
      });
      Logger.error(error as string);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action.bound
  async loadDraft(entityType: string, draftId: string): Promise<void> {
    this.entityType = entityType;
    this.isPublicSubmission = false;
    const isAudits = isAuditsSubmission(entityType);
    try {
      this.isLoading = true;
      const { data } = await this.api.entities.getDraft(entityType, draftId)();

      // Audits keep the server's visited path: autosave echoes the stack back, so truncating it
      // permanently drops answers outside section 1 at submit.
      const stack = isAudits ? data.stack : data.stack.slice(0, 1);
      const hasNext = isAudits ? !!data.hasNext : data.hasNext || data.stack.length > 1;

      runInAction(() => {
        this.data = { ...data, stack, hasNext };
        this.draftActivities = new DraftActivities(this.rootStore, this.api, entityType, draftId);
        this.questionActivities = isAudits
          ? new AuditQuestionActivities(this.rootStore, this.api, { entityType, host: 'draft', hostId: draftId })
          : undefined;
      });
    } catch (error) {
      Logger.error(error as string);
      // redirect to list view on 404
      if ((error as AxiosError).response?.status === 404) {
        this.rootStore.routing.push(getEntitySubmissionConfig(entityType).listRoute);
      }
      runInAction(() => {
        this.isError = true;
      });
    }
    this.isLoading = false;
  }

  @action.bound
  getCurrentSectionProperties(): FormBuilderProperties | undefined {
    if (!this.data) {
      return undefined;
    }

    const currentSection = this.data.stack[this.data.stack.length - 1];

    return this.data.form.form.properties[currentSection];
  }

  // An arrow class property, not an @action method: this is a pure read, and wrapping reads in
  // an action breaks the observer dependency tracking of any component that calls it during
  // render. Arrow syntax keeps it bound for destructured use (`const { getContentForKey } = useStore()`).
  getContentForKey = (key: string, sectionKey?: string): AnswerTypes | undefined => {
    if (!this.data) {
      return undefined;
    }

    if (sectionKey) {
      const section = this.data.form.form.properties[sectionKey];
      const { questionType } = section.properties[key];

      if (questionType === ProtectorType.MultiChoice || questionType === ProtectorType.SingleChoice) {
        const enumOptions = section.properties[key].properties?.enum as unknown as EnumOptions;
        const content = this.data.content[key];

        if (!content) {
          return undefined;
        }

        const keyToValueMap = Object.fromEntries(enumOptions.map((item) => [item.key, item.value])) as Record<
          string,
          string
        >;

        return (content as string[]).map((k) => keyToValueMap[k]);
      }
    }

    return this.data.content[key];
  };

  @action.bound
  getErrorForKey(key: string): string[] | undefined {
    return this.contentErrors[key];
  }

  checkFormVersion(newVersion: string): boolean {
    if (this.data?.formVersion !== newVersion) {
      if (this.isPublicSubmission) {
        this.rootStore.notification.enqueueWarningSnackbar(
          'The form layout has been updated and cannot be submitted. Please refresh the page to load the latest version and try again.',
          { autoClose: false }
        );
      } else {
        this.showRedirectModal = true;
      }
      return true;
    }
    return false;
  }

  @action.bound
  setAutoSaving(autoSaving: boolean): void {
    runInAction(() => {
      this.autoSaving = autoSaving;
    });
  }

  @action.bound
  markEditPending(key: string): void {
    runInAction(() => {
      this.pendingEdits.add(key);
      this.syncAutoSaving();
    });
  }

  private syncAutoSaving(): void {
    this.setAutoSaving(this.pendingEdits.size > 0 || this.autosaveQueue.size > 0 || this.autosaveInFlight);
  }

  @action.bound
  async updateContentForKey(key: string, value: AnswerTypes, type?: ProtectorType): Promise<void> {
    if (this.pendingEdits.delete(key)) {
      this.syncAutoSaving();
    }

    if (!this.data || !this.entityType) {
      return;
    }
    // if this is a public submission do not send data to the server
    // just store it in the data
    if (this.isPublicSubmission) {
      let updatedValue = value;

      // if value is empty array or empty string set it to null
      if (value === '' || (Array.isArray(value) && value.length === 0)) {
        updatedValue = null;
      }

      this.data.content[key] = updatedValue;
      this.requiredNotFilled = this.requiredNotFilled.filter((item) => item !== key);
      // Clear errors for this field when updating
      if (this.contentErrors[key]) {
        delete this.contentErrors[key];
      }

      // Update IndexedDB record with the new content
      publicEventFormDB
        .updatePublicEventSubmission(this.publicFormSessionId, this.publicSubmissionUuid, {
          content: this.data.content,
          stack: this.data.stack,
        })
        .catch((error) => {
          Logger.error('Failed to update IndexedDB record:', error);
          // Don't fail the form update if IndexedDB fails
        });
      return;
    }

    let updatedValue = value;

    // if value is empty array or empty string set it to null
    if (value === '' || (Array.isArray(value) && value.length === 0)) {
      updatedValue = null;
    }

    runInAction(() => {
      if (!this.lastSaved.has(key)) {
        this.lastSaved.set(key, this.data!.content[key]);
      }

      this.autosaveQueue.set(key, { value: updatedValue, type });
      this.keyWriteSeq.set(key, ++this.writeSeq);
      this.data!.content[key] = updatedValue;
      this.requiredNotFilled = this.requiredNotFilled.filter((item) => item !== key);
      this.pendingEdits.delete(key);
      this.syncAutoSaving();
    });

    await this.drainAutosaveQueue();
  }

  private drainAutosaveQueue(): Promise<void> {
    if (!this.autosaveDrain) {
      this.autosaveDrain = this.runAutosaveQueue().finally(() => {
        this.autosaveDrain = null;
      });
    }

    return this.autosaveDrain;
  }

  private async runAutosaveQueue(): Promise<void> {
    while (this.autosaveQueue.size > 0) {
      const [key, entry] = this.autosaveQueue.entries().next().value as [string, AutosaveQueueEntry];
      const seq = this.keyWriteSeq.get(key) as number;

      runInAction(() => {
        this.autosaveQueue.delete(key);
        this.autosaveInFlight = true;
      });

      try {
        await this.sendAutosave(key, entry, seq);
      } finally {
        runInAction(() => {
          this.autosaveInFlight = false;
          this.syncAutoSaving();
        });
      }
    }
  }

  private async sendAutosave(key: string, entry: AutosaveQueueEntry, seq: number): Promise<void> {
    if (!this.data || !this.entityType) {
      return;
    }

    const { uuid, stack } = this.data;
    const { value: updatedValue, type } = entry;

    // Collect files if this is an attachment field
    let files: File[] | undefined;
    const uploadedFilenames: string[] = [];
    if (type === ProtectorType.Attachment && updatedValue && Array.isArray(updatedValue)) {
      const fieldUploads = this.fileUploadManager.getFieldUploads(key);

      if (fieldUploads.length > 0) {
        // Identify all matching files (attachment items with corresponding upload items)
        updatedValue.forEach((item) => {
          // Check if item is AttachmentType and has matching upload item
          if (typeof item === 'object' && 'filename' in item) {
            const uploadItem = fieldUploads.find((u) => u.file.name === item.filename);
            if (uploadItem) {
              uploadedFilenames.push(item.filename);
              this.fileUploadManager.updateStatus(key, item.filename, FileUploadStatus.Uploading);
              files = [...(files || []), uploadItem.file];
            }
          }
        });
      }
    }

    try {
      const { data } = await this.api.entities.updateEntityFormAutosave(
        this.entityType,
        this.isPublicSubmission,
        this.isPublicSubmission ? this.publicSubmissionUuid : uuid,
        {
          content: { [key]: updatedValue },
          stack,
          publicSubmissionDetails: this.publicSubmissionUserDetails,
          ...(this.isPublicSubmission ? { publicFormSessionId: this.publicFormSessionId } : {}),
        },
        files
      )();

      // A different draft can be loaded while the request is open; this response isn't for it.
      if (this.data?.uuid !== uuid) {
        return;
      }

      if (this.checkFormVersion(data.formVersion)) {
        runInAction(() => {
          this.autosaveQueue.clear();
          this.lastSaved.clear();
        });
        return;
      }

      runInAction(() => {
        // The server reshapes lookup/user answers and adds url/size to attachments.
        this.settleLastSaved(key, data.content[key]);
        this.applyAutosaveResponse(data);
        // Clear errors for this field on successful update
        if (this.contentErrors[key]) {
          delete this.contentErrors[key];
        }

        const isStale = this.keyWriteSeq.get(key) !== seq;
        if (!isStale) {
          // applyAutosaveResponse skips `key` while it's still queued, so this response's own
          // reshaped value (e.g. attachment url/size) must be applied here instead.
          if (this.data) {
            this.data.content[key] = data.content[key];
          }

          // Mark files as successfully uploaded and remove them from pending
          uploadedFilenames.forEach((filename) => {
            this.fileUploadManager.updateStatus(key, filename, FileUploadStatus.Success);
            this.fileUploadManager.removeFile(key, filename);
          });
        }

        // clean up all deleting files
        this.fileUploadManager.clearAllDeletingFiles();
      });
    } catch (error) {
      const e = error as AxiosError;
      const responseData = e.response?.data as { content?: Record<string, string[]> } | undefined;

      if (e.response?.status === 400 && responseData?.content) {
        runInAction(() => {
          this.contentErrors = { ...this.contentErrors, ...responseData.content };
        });
        this.rootStore.notification.enqueueErrorSnackbar('Please check your input and try again.');
      } else {
        Logger.error(error as string);
        this.rootStore.notification.enqueueErrorSnackbar('Failed to save changes');
      }

      runInAction(() => {
        const isStale = this.keyWriteSeq.get(key) !== seq;

        // in case of error revert the value
        if (this.data?.uuid === uuid && !isStale && !this.pendingEdits.has(key)) {
          const revertTo = this.lastSaved.get(key) as AnswerTypes;
          this.data.content[key] = revertTo;
          this.settleLastSaved(key, revertTo);
        }
        // Mark the pending files that failed to upload as 'failed'
        if (!isStale && type === ProtectorType.Attachment && uploadedFilenames.length > 0) {
          uploadedFilenames.forEach((filename) => {
            this.fileUploadManager.updateStatus(key, filename, FileUploadStatus.Failed);
          });
        }
      });
    }
  }

  private settleLastSaved(key: string, savedValue: AnswerTypes): void {
    if (this.autosaveQueue.has(key) || this.pendingEdits.has(key)) {
      this.lastSaved.set(key, savedValue);
    } else {
      this.lastSaved.delete(key);
    }
  }

  // Never `this.data = data`: the response predates any answer saved since, and its `stack` is the
  // section the PATCH was sent from, so assigning it wholesale reverts both.
  private applyAutosaveResponse(data: EntitySubmissionDraftType): void {
    const draft = this.data;

    if (!draft) {
      return;
    }

    draft.updatedAt = data.updatedAt;

    Object.entries(data.content).forEach(([key, serverValue]) => {
      if (this.autosaveQueue.has(key)) {
        return;
      }

      draft.content[key] = serverValue;
    });
  }

  @action.bound
  async getPreview(): Promise<void> {
    if (!this.data || !this.entityType) {
      return;
    }

    const dataToSend = this.isPublicSubmission
      ? {
          content: this.transformContentToSend(this.data.content),
          stack: this.data.stack,
          publicSubmissionDetails: this.publicSubmissionUserDetails,
          publicFormSessionId: this.publicFormSessionId,
        }
      : undefined;

    try {
      const { data } = await this.api.entities.getPreview(
        this.entityType,
        this.isPublicSubmission,
        this.isPublicSubmission ? this.publicSubmissionUuid : this.data.uuid,
        dataToSend
      )();

      if (this.checkFormVersion(data.formVersion)) {
        return;
      }

      runInAction(() => {
        this.previewData = data;
        this.showPreview = true;
        this.data = data;
        this.updating = false;
      });
    } catch (error) {
      Logger.error(error as string);
      this.rootStore.notification.enqueueErrorSnackbar('Failed to get preview');
    }
  }

  @action.bound
  async onNextClick(): Promise<void> {
    if (!this.data || !this.entityType) {
      return;
    }

    // Client pre-check for repeating-group required sub-fields — instant per-field feedback without a
    // round-trip; the backend re-validates as the safety net.
    if (!this.validateRepeatingGroupsForCurrentSection()) {
      this.rootStore.notification.enqueueErrorSnackbar('You have to fill in all required fields.');
      return;
    }

    try {
      this.updating = true;

      const dataToSend = this.isPublicSubmission
        ? {
            content: this.transformContentToSend(this.data.content),
            stack: this.data.stack,
            publicSubmissionDetails: this.publicSubmissionUserDetails,
            publicFormSessionId: this.publicFormSessionId,
          }
        : { stack: this.data.stack };

      const { data } = await this.api.entities.getNextSection(
        this.entityType,
        this.isPublicSubmission,
        this.isPublicSubmission ? this.publicSubmissionUuid : this.data.uuid,
        dataToSend
      )();

      if (data.hasNext === false) {
        await this.getPreview();
        return;
      }

      if (this.checkFormVersion(data.formVersion)) {
        this.updating = false;
        return;
      }

      runInAction(() => {
        this.data = data;
        this.requiredNotFilled = [];
      });
    } catch (error) {
      this.updating = false;
      const e = error as AxiosError;
      const responseData = e.response?.data as { content?: Record<string, string[]> } | undefined;
      if (e.response?.status === 400 && responseData?.content) {
        runInAction(() => {
          this.requiredNotFilled = Object.keys(responseData.content || {});
          this.contentErrors = { ...this.contentErrors, ...responseData.content };
        });
        this.rootStore.notification.enqueueErrorSnackbar('You have to fill in all required fields.');
      } else {
        Logger.error(error as string);
        this.rootStore.notification.enqueueErrorSnackbar('Failed to save changes');
      }
    }
    this.updating = false;
  }

  @action.bound
  async onPreviousClick(updatedStackProp?: string[]): Promise<void> {
    if (!this.data || !this.entityType) {
      return;
    }

    try {
      this.showPreview = false;
      this.updating = true;
      const updatedStack = updatedStackProp || this.data.stack.slice(0, -1);
      const dataToSend = this.isPublicSubmission
        ? {
            content: this.transformContentToSend(this.data.content),
            stack: updatedStack,
            publicSubmissionDetails: this.publicSubmissionUserDetails,
            publicFormSessionId: this.publicFormSessionId,
          }
        : {
            stack: updatedStack,
          };

      const { data } = await this.api.entities.getPreviousSection(
        this.entityType,
        this.isPublicSubmission,
        this.isPublicSubmission ? this.publicSubmissionUuid : this.data.uuid,
        dataToSend
      )();

      if (this.checkFormVersion(data.formVersion)) {
        return;
      }

      runInAction(() => {
        this.data = data;
        this.clearSectionValidation();
      });
    } catch (error) {
      Logger.error(error as string);
    }
    this.updating = false;
  }

  // Errors belong to the section that failed to advance; left in place they disable Next on the one we return to.
  private clearSectionValidation(): void {
    this.requiredNotFilled = [];
    this.contentErrors = {};
    this.repeatingGroupErrors = {};
  }

  @action.bound
  async editSection(section: string): Promise<void> {
    if (!this.data) {
      return;
    }
    this.showPreview = false;
    // find the section in the stack and truncate the stack to that section
    const index = this.data.stack.indexOf(section);
    if (index !== -1) {
      this.data.hasNext = true;
      await this.onPreviousClick(this.data.stack.slice(0, index + 1));
    }
  }

  private transformContentToSend(data: Record<string, AnswerTypes>): Record<string, AnswerTypes> {
    const content = { ...data };

    Object.keys(data).forEach((key) => {
      const value = data[key];
      // if it is an object and it has uuid property, set it to uuid
      if (value && typeof value === 'object' && !Array.isArray(value) && 'uuid' in value) {
        content[key] = value.uuid;
      } else {
        content[key] = value;
      }
    });

    return content;
  }

  @action.bound
  changeShowConfirmPopup(value: boolean): void {
    this.showConfirmPopup = value;
  }

  @action.bound
  setExistingDraftSessionId(sessionId: string | null): void {
    this.existingDraftSessionId = sessionId;
  }

  @action.bound
  async checkForExistingDrafts(formId: string): Promise<string | null> {
    try {
      const draftSubmissions = await publicEventFormDB.getDraftSubmissionsByFormId(formId);

      if (draftSubmissions.length > 0) {
        const draftContent = draftSubmissions[0].content;

        if (Object.keys(draftContent).length > 0) {
          // Use the most recent draft (assuming they're sorted by creation date)
          const mostRecentDraft = draftSubmissions[draftSubmissions.length - 1];
          return mostRecentDraft.uuid;
        }

        // remove empty draft and anything else older than that
        await publicEventFormDB.deleteAllDraftSubmissionsByFormId(formId);

        return null;
      }

      return null;
    } catch (error) {
      Logger.error('Failed to check for existing drafts:', error);
      // Don't fail the form loading if draft check fails
      return null;
    }
  }

  @action.bound
  async updateUserDetailsStep(): Promise<void> {
    if (!this.data?.form?.settings) {
      return;
    }

    const settings = this.data.form.settings.publicSubmission;

    if (settings?.name || settings?.email || settings?.phone) {
      this.publicSubmissionUserDetailsFields = settings;
      this.displayUserDetailsStep = true;
    } else {
      this.publicSubmissionUserDetailsFields = null;
      this.displayUserDetailsStep = false;
      this.publicSubmissionUserDetails = undefined;
    }
  }

  @action.bound
  async continueWithDraft(): Promise<void> {
    if (!this.existingDraftSessionId) {
      return;
    }

    // Update the session ID to the existing draft's session ID
    this.publicFormSessionId = this.existingDraftSessionId;

    // Close the modal
    this.questionActivities = undefined;
    this.showDraftContinuationModal = false;
    this.existingDraftSessionId = null;

    // Load the existing draft data
    try {
      const draftData = await publicEventFormDB.getPublicEventSubmission(this.publicFormSessionId);

      // Update the form data with the existing draft content
      if (draftData) {
        runInAction(() => {
          if (this.data && draftData) {
            const draftSubmission = draftData as {
              content: Record<string, AnswerTypes>;
              stack: string[];
            };
            // Make sure data change will be tracked by MobX
            this.data = {
              ...this.data,
              content: draftSubmission.content || {},
              stack: draftSubmission.stack || [this.data.stack[0]],
            };
          }
        });

        // Restore attachment files from IndexedDB
        await this.fileUploadManager.restoreFromIndexedDB(this.publicFormSessionId);

        // Check if any files referenced in content are missing after restoration
        if (this.missingAttachmentFields.length > 0) {
          this.showMissingFilesModal = true;
        }

        this.updateUserDetailsStep();
      }
    } catch (error) {
      Logger.error('Failed to load draft data:', error);
      this.rootStore.notification.enqueueErrorSnackbar('Failed to load existing draft');
    }
  }

  @action.bound
  async removeDraftAndStartNewSubmission(publicFormId: string): Promise<void> {
    if (!this.existingDraftSessionId) {
      return;
    }

    // Delete attachment files for the existing draft
    try {
      await publicEventFormDB.deleteAllAttachmentFiles(this.existingDraftSessionId);
      Logger.debug('Attachment files deleted for draft:', this.existingDraftSessionId);
    } catch (error) {
      Logger.error('Failed to delete attachment files for draft:', error);
    }

    // Delete the existing drafts
    try {
      await publicEventFormDB.deleteAllDraftSubmissionsByFormId(publicFormId);
      Logger.debug('Existing draft deleted:', this.existingDraftSessionId);
    } catch (error) {
      Logger.error('Failed to delete existing draft:', error);
      // Don't fail the process if deletion fails
    }

    // Close the modal and reset state
    this.showDraftContinuationModal = false;
    this.existingDraftSessionId = null;
    this.loadPublicSubmission(publicFormId);
  }

  @action.bound
  async onSubmit(): Promise<void> {
    if (!this.data || !this.entityType || this.submitting) {
      return;
    }

    // Check for missing attachment files (files referenced in content but not in FileUploadManager)
    if (this.isPublicSubmission && this.missingAttachmentFields.length > 0) {
      this.showMissingFilesModal = true;
      return;
    }

    this.submitting = true;

    // For public submissions, verify the form version hasn't changed since we loaded it
    if (this.isPublicSubmission) {
      try {
        const { data: latestData } = await this.api.entities.getPublicSubmission(
          this.entityType,
          this.publicSubmissionUuid,
          this.publicFormSessionId
        )();

        if (this.checkFormVersion(latestData.formVersion)) {
          this.submitting = false;
          return;
        }
      } catch (error) {
        Logger.error('Failed to verify form version before submit:', error as string);
      }
    }

    const dataToSend = this.isPublicSubmission
      ? {
          content: this.transformContentToSend(this.data.content),
          stack: this.data.stack,
          publicSubmissionDetails: this.publicSubmissionUserDetails,
          publicSubmissionUuid: this.publicSubmissionUuid,
          publicFormSessionId: this.publicFormSessionId,
        }
      : undefined;

    // Collect all pending files from all fields
    const files = this.fileUploadManager.getAllPendingFiles();
    const hasFiles = files.length > 0;

    try {
      const { data, status } = await this.api.entities.submitEntityForm(
        this.entityType,
        this.isPublicSubmission,
        this.isPublicSubmission ? this.publicSubmissionUuid : this.data.uuid,
        dataToSend,
        hasFiles ? files : undefined
      )();

      // if publicSubmission send also submissionActivities
      if (this.isPublicSubmission) {
        if (
          this.submissionActivities &&
          this.submissionActivities.activities &&
          this.submissionActivities.activities?.length > 0
        ) {
          await this.submissionActivities?.addSubmissionActivity(data.uuid);
        } else {
          this.submitting = false;
          this.changeShowConfirmPopup(true);
          await new Promise((resolve) => setTimeout(resolve, 2000));
          this.changeShowConfirmPopup(false);

          this.submissionActivities?.changeFinishedUploading(true);
        }

        if (status === 202) {
          // Offline submission - DON'T clear files, service worker needs them for sync
          this.displayOfflineSuccessPage = true;
        } else {
          // Online submission successful - clear pending files from memory
          this.clearPendingFiles();
          // Clean up IndexedDB records after successful online submission
          if (this.publicFormSessionId) {
            // Delete attachment files first, then the submission record
            publicEventFormDB.deleteAllAttachmentFiles(this.publicFormSessionId).catch((error) => {
              Logger.error('Failed to clean up attachment files from IndexedDB:', error);
            });
            publicEventFormDB.deletePublicEventSubmission(this.publicFormSessionId);
          }
          this.displaySuccessPage = true;
        }
      } else {
        // Non-public submission - clear pending files
        this.clearPendingFiles();
        this.showSubmittedModal = true;
        await new Promise((resolve) => setTimeout(resolve, 2000));
        this.showSubmittedModal = false;
        this.rootStore.routing.push(getEntitySubmissionConfig(this.entityType).recordUrl(data.uuid));
      }
    } catch (error) {
      const e = error as AxiosError;
      const responseData = e.response?.data as { content?: Record<string, unknown> } | undefined;
      // A 400 with field errors (e.g. a required procedure sub-field left empty) is a validation
      // failure, not a crash — tell the user why instead of a generic "failed" toast.
      if (e.response?.status === 400 && responseData?.content) {
        this.rootStore.notification.enqueueErrorSnackbar('You have to fill in all required fields.');
      } else {
        Logger.error(error as string);
        this.rootStore.notification.enqueueErrorSnackbar(
          `Failed to submit the ${getEntitySubmissionConfig(this.entityType).recordLabel.toLowerCase()}`
        );
      }
      // Don't clear pending files on error - user might want to retry
    } finally {
      this.submitting = false;
    }
  }

  // offline methods
  @action.bound
  onNextClickOffline(): void {
    if (!this.data || !this.entityType) return;

    if (!allRequiredFieldsFilled(this.data)) {
      this.requiredNotFilled = invalidFields(this.data);

      return;
    }

    if (!this.validateRepeatingGroupsForCurrentSection()) {
      this.rootStore.notification.enqueueErrorSnackbar('You have to fill in all required fields.');
      return;
    }

    const result = determineNextSection(this.data);

    if (result.showPreview) {
      this.getPreviewOffline();
    } else {
      runInAction(() => {
        if (!this.data) return;
        // need to update the data with the new stack
        this.data = { ...this.data, stack: result.stack };
      });
    }
  }

  @action.bound
  onPreviousClickOffline(updatedStackProp?: string[]): void {
    if (!this.data || !this.entityType) return;

    const { stack } = this.data;
    const updatedStack = updatedStackProp || stack.slice(0, -1);

    // Updating the stack will trigger a re-render of the SubmissionSection component
    this.data = { ...this.data, stack: updatedStack };
    this.clearSectionValidation();
  }

  @action.bound
  getPreviewOffline(): void {
    if (!this.data || !this.entityType) return;

    runInAction(() => {
      this.previewData = this.data;
      this.showPreview = true;
    });
  }

  @action.bound
  async updateContentForKeyOffline(key: string, value: AnswerTypes): Promise<void> {
    if (!this.data || !this.entityType) return;

    let updatedValue = value;

    if (value === '' || (Array.isArray(value) && value.length === 0)) {
      updatedValue = null;
    }

    runInAction(() => {
      // Create a new content object to ensure MobX detects the change
      const newContent = { ...this.data!.content };

      if (updatedValue === null) {
        // Delete the key to ensure MobX detects the change when removing values
        delete newContent[key];
        if (fieldIsRequired(this.data!, this.currentSection, key)) {
          this.requiredNotFilled.push(key);
        }
      } else {
        newContent[key] = updatedValue;
        this.requiredNotFilled = this.requiredNotFilled.filter((item) => item !== key);
      }

      // Replace the entire data object to ensure MobX reactivity
      this.data = {
        ...this.data!,
        content: newContent,
      };

      // Clear errors for this field when updating
      if (this.contentErrors[key]) {
        delete this.contentErrors[key];
      }
    });

    // Update IndexedDB record with the new content for offline submissions
    if (this.isPublicSubmission) {
      publicEventFormDB
        .updatePublicEventSubmission(this.publicFormSessionId, this.publicSubmissionUuid, {
          content: this.data.content,
          stack: this.data.stack,
        })
        .catch((error) => {
          Logger.error('Failed to update IndexedDB record for offline submission:', error);
          // Don't fail the form update if IndexedDB fails
        });
    }
  }

  @action.bound
  editSectionOffline(section: string): void {
    if (!this.data || !this.entityType) return;
    this.showPreview = false;

    const { stack } = this.data;
    const index = stack.indexOf(section);

    if (index !== -1) {
      this.data.stack = stack.slice(0, index + 1);
    }
  }

  @action.bound
  reset(): void {
    this.data = null;
    this.isError = false;
    this.updating = false;
    this.entityType = null;
    this.isLoading = false;
    this.autoSaving = false;
    this.autosaveQueue.clear();
    this.pendingEdits.clear();
    this.keyWriteSeq.clear();
    this.lastSaved.clear();
    this.submitting = false;
    this.previewData = null;
    this.showPreview = false;
    this.requiredNotFilled = [];
    this.repeatingGroupErrors = {};
    this.showRedirectModal = false;
    this.showSubmittedModal = false;
    this.showMissingFilesModal = false;

    this.displaySuccessPage = false;
    this.displayOfflineSuccessPage = false;
    this.publicSubmissionUserDetails = {
      name: null,
      email: null,
      phone: null,
    };
    this.publicSubmissionUserDetailsFields = null;
    this.displayUserDetailsStep = false;
    this.userDetailsRequiredNotFilled = [];
    this.userDetailsErrors = {};
    this.contentErrors = {};
    this.publicFormSessionId = '';
    this.showDraftContinuationModal = false;
    this.existingDraftSessionId = null;
    this.clearPendingFiles();
  }
}
